// A staff change is written under the signature picked in the user menu, else the login.
export const actingAs = (
  restaurantId: string | null | undefined,
  uid: string | null
) => {
  try {
    const signature = JSON.parse(
      window.localStorage.getItem(`${restaurantId}-globalSignature`) ?? '{}'
    )?.value;

    return typeof signature === 'string' && signature.trim() ? signature : uid;
  } catch {
    return uid;
  }
};

// The menu signature must not carry over to another login on this device.
export const forgetSignaturesOfOtherLogin = (uid: string) => {
  try {
    const lastLogin = window.localStorage.getItem('globalSignatureLogin');

    if (lastLogin && lastLogin !== uid) {
      Object.keys(window.localStorage)
        .filter((key) => key.endsWith('-globalSignature'))
        .forEach((key) => window.localStorage.removeItem(key));
    }

    window.localStorage.setItem('globalSignatureLogin', uid);
  } catch {
    // A storage that throws must not stop the login that calls this.
  }
};

// Login ids are 20 to 28 letters and digits, signatures are shorter or contain other characters.
const isUserId = (uid: string) => /^[A-Za-z0-9]{20,28}$/.test(uid);

// Older history entries carry a job or a note here instead of a user.
const FIXED_AUTHORS = new Map([
  ['system', 'System'],
  ['guest', 'Gast'],
  ['voice-agent-api', 'Voice-Agent'],
  ['sia', 'Sia'],
  ['GNAUT', 'System'],
  ['computedFields', 'System'],
  ['projectGuestCustomFields', 'System'],
  ['merge-guests', 'System'],
  ['Kassensystem', 'Kassensystem'],
  ['Reservierung wurde von Gast bestätigt', 'Gast'],
]);

const SYSTEM_AUTHORS = ['System', 'Automatische Änderung'];

// Older entries still list these; createVersion no longer records them.
const TECHNICAL_FIELDS = [
  'confirmationEmailId',
  'calendarSequence',
  'sendConfirmationEmail',
  'emailStatus',
  'sms',
  'secondaryConfirmationEmails',
  'publicApiQuotaCountedAt',
];

const isBlank = (value: unknown) =>
  value == null ||
  value === '' ||
  (typeof value === 'object' && !Object.keys(value as object).length);

export const readableHistory = <
  T extends {
    changes?: { key: string; title?: string; before?: any; after?: any }[];
    updateNote?: { note?: string } | null;
  }
>(
  history: T[]
) =>
  history
    .map((h) => ({
      ...h,
      // A change without a title and a value would show up as an empty line.
      changes: (Array.isArray(h.changes) ? h.changes : []).filter(
        (c) =>
          !TECHNICAL_FIELDS.includes(c.key) &&
          (c.title || !isBlank(c.before) || !isBlank(c.after))
      ),
    }))
    .filter((h) => h.changes.length || h.updateNote?.note);

type Author = { uid?: string | null; name?: string | null } | null;

const isName = (name: unknown): name is string =>
  typeof name === 'string' &&
  !!name.trim() &&
  name !== 'Unb. Nutzer' &&
  name !== 'Unbekannt';

export const isNamelessLogin = (author?: Author) =>
  typeof author?.uid === 'string' &&
  isUserId(author.uid) &&
  !FIXED_AUTHORS.has(author.uid) &&
  !isName(author.name);

// Signature, else the user's name or email, else Unbekannt.
export const historyAuthor = (author?: Author) => {
  const { uid, name } = author ?? {};
  const id = typeof uid === 'string' ? uid : '';

  if (FIXED_AUTHORS.has(id)) return FIXED_AUTHORS.get(id) as string;
  if (SYSTEM_AUTHORS.includes(id)) return isName(name) ? name : 'System';
  if (id && !isUserId(id)) return id;
  if (name === 'system') return 'System';
  if (isName(name)) return name;
  return 'Unbekannt';
};
